import React, { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { useAuth, AuthModal } from '../auth/AuthComponents';
import { ThemeToggle } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import DocxsAiLogo from './DocxsAiLogo';

const Navigation = ({ showAuthModal, setShowAuthModal }) => {
  const { isAuthenticated } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const navLinks = [
    { name: 'Home', action: () => navigate('/'), current: window.location.pathname === '/' || window.location.pathname === '/home' },
    { name: 'About', action: () => navigate('/about'), current: window.location.pathname === '/about' },
    { name: 'Pricing', action: () => navigate('/pricing'), current: window.location.pathname === '/pricing' },
  ];

  const pathname = window.location.pathname;
  const path = pathname === '/' ? 'home' : pathname.split('/')[1];
  const isPublicPage = [
    'home',
    'about',
    'pricing',
    'privacy',
    'terms',
    'lawyers',
  ].includes(path);

  const brandCta =
    'brand-gradient-btn px-5 py-2.5 rounded-xl text-sm';
  const brandCtaFull =
    'brand-gradient-btn w-full px-5 py-2.5 rounded-xl';

  if (isPublicPage) {
    return (
      <>
        <nav className="absolute top-0 left-0 right-0 z-30 bg-[var(--color-surface-muted)] border-b border-[var(--color-border)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-14 sm:h-16">
              <div className="flex items-center">
                <DocxsAiLogo className="h-12 sm:h-14 w-auto" />
              </div>
              <div className="hidden lg:flex items-center space-x-2">
                {navLinks.map((link) => (
                  link.action ? (
                    <button
                      key={link.name}
                      onClick={link.action}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        link.current
                          ? 'text-[var(--color-primary)] bg-[var(--color-primary-soft)]'
                          : 'text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)]'
                      }`}
                    >
                      {link.name}
                    </button>
                  ) : (
                    <a
                      key={link.name}
                      href={link.href}
                      className="text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)] px-3 py-2 rounded-md text-sm font-medium transition-colors"
                    >
                      {link.name}
                    </a>
                  )
                ))}
              </div>
              <div className="hidden lg:flex items-center space-x-4">
                <ThemeToggle compact />
                {isAuthenticated ? (
                  <button
                    onClick={() => navigate('/dashboard')}
                    className={brandCta}
                  >
                    Dashboard
                  </button>
                ) : (
                  <button
                    onClick={() => setShowAuthModal(true)}
                    className={brandCta}
                  >
                    Get Started
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 lg:hidden">
                <ThemeToggle compact />
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="p-2 rounded-lg text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface-muted)] transition-colors"
                  aria-label="Toggle mobile menu"
                >
                  {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
            </div>
          </div>
          {mobileMenuOpen && (
            <div className="lg:hidden bg-[var(--color-surface-muted)]/95 backdrop-blur-md border-t border-[var(--color-border)]">
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
                {navLinks.map((link) => (
                  link.action ? (
                    <button
                      key={link.name}
                      onClick={() => {
                        link.action();
                        setMobileMenuOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-md text-base font-medium transition-colors ${
                        link.current
                          ? 'text-[var(--color-primary)] bg-[var(--color-primary-soft)]'
                          : 'text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)]'
                      }`}
                    >
                      {link.name}
                    </button>
                  ) : (
                    <a
                      key={link.name}
                      href={link.href}
                      className="text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)] block px-3 py-2 rounded-md text-base font-medium"
                    >
                      {link.name}
                    </a>
                  )
                ))}
              </div>
              <div className="pt-4 pb-3 border-t border-[var(--color-border)] px-5">
                {isAuthenticated ? (
                  <button
                    onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                    className={brandCtaFull}
                  >
                    Dashboard
                  </button>
                ) : (
                  <button
                    onClick={() => { setShowAuthModal(true); setMobileMenuOpen(false); }}
                    className={brandCtaFull}
                  >
                    Get Started
                  </button>
                )}
              </div>
            </div>
          )}
        </nav>
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
        />
      </>
    );
  }

  return (
    <>
      <nav className="bg-[var(--color-surface)] shadow-sm border-b border-[var(--color-border)] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button
              type="button"
              onClick={() => navigate('/home')}
              className="flex items-center"
              aria-label="DocxsAi home"
            >
              <DocxsAiLogo className="h-8 w-auto" />
            </button>

            <div className="hidden lg:flex items-center space-x-4">
              <ThemeToggle compact />
              {isAuthenticated ? (
                <button
                  onClick={() => navigate('/dashboard')}
                  className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                    window.location.pathname === '/dashboard'
                      ? 'bg-[var(--color-primary-soft)] text-[var(--color-primary)]'
                      : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                  }`}
                >
                  Dashboard
                </button>
              ) : (
                <>
                  <button
                    onClick={() => setShowAuthModal(true)}
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                  >
                    Sign In
                  </button>
                  <button
                    onClick={() => setShowAuthModal(true)}
                    className="bg-[var(--color-primary)] text-[var(--color-text-inverse)] px-4 py-2 rounded-lg hover:bg-[var(--color-primary-hover)]"
                  >
                    Get Started
                  </button>
                </>
              )}
            </div>

            <div className="flex items-center gap-2 lg:hidden">
              <ThemeToggle compact />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-lg text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-surface-muted)] transition-colors"
                aria-label="Toggle mobile menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden bg-[var(--color-surface)] border-t border-[var(--color-border)]">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {navLinks.map((link) => (
                link.action ? (
                  <button
                    key={link.name}
                    onClick={() => {
                      link.action();
                      setMobileMenuOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 rounded-md text-base font-medium transition-colors ${
                      link.current
                        ? 'text-[var(--color-primary)] bg-[var(--color-primary-soft)]'
                        : 'text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)]'
                    }`}
                  >
                    {link.name}
                  </button>
                ) : (
                  <a
                    key={link.name}
                    href={link.href}
                    className="text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)] block px-3 py-2 rounded-md text-base font-medium"
                  >
                    {link.name}
                  </a>
                )
              ))}
            </div>
            <div className="pt-4 pb-3 border-t border-[var(--color-border)] px-5">
              {isAuthenticated ? (
                <button
                  onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                  className="w-full bg-[var(--color-primary)] text-[var(--color-text-inverse)] px-5 py-2.5 rounded-lg hover:bg-[var(--color-primary-hover)] transition-colors font-semibold"
                >
                  Dashboard
                </button>
              ) : (
                <button
                  onClick={() => { setShowAuthModal(true); setMobileMenuOpen(false); }}
                  className="w-full bg-[var(--color-primary)] text-[var(--color-text-inverse)] px-5 py-2.5 rounded-lg hover:bg-[var(--color-primary-hover)] transition-colors font-semibold"
                >
                  Get Started
                </button>
              )}
            </div>
          </div>
        )}
      </nav>
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </>
  );
};

export default Navigation;
